import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Grid, Paper, Typography, Button, Chip, Divider, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  Tabs, Tab, Avatar, IconButton, Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import PeopleIcon from "@material-ui/icons/People";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { toast } from "react-toastify";
import api from "../../services/partnerApi";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: 12,
  },
  logo: {
    fontWeight: 800,
    fontSize: 22,
    background: "linear-gradient(90deg, #f59e0b, #ef4444)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  card: {
    background: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 16,
    padding: theme.spacing(2.5),
    color: "#fff",
  },
  statValue: { fontSize: 32, fontWeight: 800, lineHeight: 1.2 },
  statLabel: { fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 4 },
  referralBox: {
    background: "rgba(245,158,11,0.08)",
    border: "2px dashed rgba(245,158,11,0.4)",
    borderRadius: 12,
    padding: theme.spacing(2),
    color: "#fff",
  },
  referralLink: {
    fontSize: 14,
    wordBreak: "break-all",
    color: "#f59e0b",
    fontFamily: "monospace",
    background: "rgba(245,158,11,0.1)",
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    display: "block",
    marginTop: 8,
  },
  tab: { color: "rgba(255,255,255,0.6)", "&.Mui-selected": { color: "#f59e0b" } },
  tableHead: { "& th": { color: "rgba(255,255,255,0.5)", fontSize: 12, borderBottom: "1px solid rgba(255,255,255,0.1)" } },
  tableRow: { "& td": { color: "#fff", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: 13 } },
  btn: {
    background: "linear-gradient(90deg, #f59e0b, #ef4444)",
    color: "#fff",
    fontWeight: 700,
    borderRadius: 10,
    "&:hover": { background: "linear-gradient(90deg, #d97706, #dc2626)" },
  },
  btnOutline: {
    color: "#f59e0b",
    borderColor: "#f59e0b",
    borderRadius: 10,
    "&:hover": { background: "rgba(245,158,11,0.1)" },
  },
  pendingChip: { background: "#f59e0b22", color: "#f59e0b", border: "1px solid #f59e0b55" },
  paidChip: { background: "#10b98122", color: "#10b981", border: "1px solid #10b98155" },
  rejectedChip: { background: "#ef444422", color: "#ef4444", border: "1px solid #ef444455" },
  approvedChip: { background: "#3b82f622", color: "#3b82f6", border: "1px solid #3b82f655" },
  dialogPaper: {
    background: "#1e2a4a",
    color: "#fff",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.1)",
  },
  dialogInput: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
      "&.Mui-focused fieldset": { borderColor: "#f59e0b" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
  },
}));

const statusMap = {
  pending: { label: "Aguardando", className: "pendingChip" },
  approved: { label: "Aprovado", className: "approvedChip" },
  paid: { label: "Pago", className: "paidChip" },
  rejected: { label: "Rejeitado", className: "rejectedChip" },
  credited: { label: "Creditado", className: "paidChip" },
};

const PartnerDashboard = ({ partner: initialPartner, onLogout }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawNotes, setWithdrawNotes] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("partnerToken")}` }
  });

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/partners/profile", authHeader());
      setProfile(data);
    } catch (err) {
      if (err?.response?.status === 401) { onLogout(); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const copyLink = () => {
    navigator.clipboard.writeText(profile.partner.referralLink);
    toast.success("Link copiado!");
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt < 50) { toast.error("Valor mínimo para saque é R$ 50,00."); return; }
    setWithdrawLoading(true);
    try {
      await api.post("/partners/withdrawals", {
        amount: amt,
        pixKey: profile.partner.pixKey,
        notes: withdrawNotes
      }, authHeader());
      toast.success("Solicitação de saque enviada com sucesso!");
      setWithdrawDialog(false);
      setWithdrawAmount("");
      setWithdrawNotes("");
      loadProfile();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao solicitar saque.");
    } finally {
      setWithdrawLoading(false);
    }
  };

  const chipClass = (status) => classes[statusMap[status]?.className || "pendingChip"];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"
        style={{ background: "linear-gradient(135deg, #1a1a2e, #0f3460)" }}>
        <CircularProgress style={{ color: "#f59e0b" }} />
      </Box>
    );
  }

  const { partner, stats, recentCommissions, pendingWithdrawals } = profile;

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Box>
          <Typography className={classes.logo}>ChatPrime</Typography>
          <Typography variant="body2" style={{ color: "rgba(255,255,255,0.5)" }}>
            Olá, <strong style={{ color: "#fff" }}>{partner.name}</strong>!
            {partner.status === "pending" && (
              <Chip size="small" label="Cadastro em análise" style={{ marginLeft: 8, background: "#f59e0b22", color: "#f59e0b", border: "1px solid #f59e0b55" }} />
            )}
          </Typography>
        </Box>
        <Button variant="outlined" className={classes.btnOutline} startIcon={<ExitToAppIcon />} onClick={onLogout}>
          Sair
        </Button>
      </div>

      {/* Cards de estatísticas */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        {[
          { icon: <AccountBalanceWalletIcon />, value: `R$ ${Number(stats.balance).toFixed(2)}`, label: "Saldo disponível", color: "#10b981" },
          { icon: <TrendingUpIcon />, value: `R$ ${Number(stats.totalEarned).toFixed(2)}`, label: "Total ganho", color: "#f59e0b" },
          { icon: <PeopleIcon />, value: stats.totalClients, label: "Clientes indicados", color: "#3b82f6" },
          { icon: <MonetizationOnIcon />, value: `R$ ${Number(stats.pendingAmount).toFixed(2)}`, label: "Saques em andamento", color: "#8b5cf6" },
        ].map((item, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Paper className={classes.card} elevation={0}>
              <Box display="flex" alignItems="center" mb={1} style={{ color: item.color }}>
                {item.icon}
              </Box>
              <Typography className={classes.statValue} style={{ color: item.color }}>{item.value}</Typography>
              <Typography className={classes.statLabel}>{item.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Link de indicação */}
      <Paper className={classes.card} elevation={0} style={{ marginBottom: 24 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>
          🔗 Seu Link de Indicação
        </Typography>
        <div className={classes.referralBox}>
          <Typography variant="body2" style={{ color: "rgba(255,255,255,0.7)" }}>
            Compartilhe este link. Quando o cliente se cadastrar e pagar a 1ª mensalidade,
            você recebe <strong style={{ color: "#f59e0b" }}>50%</strong> automaticamente no seu saldo.
          </Typography>
          <span className={classes.referralLink}>{partner.referralLink}</span>
          <Box mt={1} display="flex" gap={1}>
            <Button size="small" startIcon={<FileCopyIcon />} onClick={copyLink} className={classes.btn}>
              Copiar Link
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={classes.btnOutline}
              href={`https://wa.me/?text=Experimente%20o%20ChatPrime%20gratuitamente!%20${encodeURIComponent(partner.referralLink)}`}
              target="_blank"
            >
              📱 Compartilhar WhatsApp
            </Button>
          </Box>
        </div>
        <Box mt={2} display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" style={{ color: "rgba(255,255,255,0.5)" }}>
            Código: <strong style={{ color: "#f59e0b", fontFamily: "monospace", fontSize: 16 }}>{partner.referralCode}</strong>
          </Typography>
        </Box>
      </Paper>

      {/* Botão saque */}
      {partner.status === "pending" ? (
        <Box mb={3} display="flex" alignItems="center" style={{
          background: "rgba(245,158,11,0.08)",
          border: "1px solid rgba(245,158,11,0.3)",
          borderRadius: 12,
          padding: "12px 16px",
        }}>
          <Typography variant="body2" style={{ color: "#f59e0b" }}>
            ⏳ Seu cadastro está em análise. Após a aprovação do administrador você poderá solicitar saques.
          </Typography>
        </Box>
      ) : (
        <Box mb={3} display="flex" alignItems="center" style={{ gap: 16, flexWrap: "wrap" }}>
          <Tooltip title={Number(stats.balance) < 50 ? `Saldo mínimo para saque é R$ 50,00 (você tem R$ ${Number(stats.balance).toFixed(2)})` : ""} arrow>
            <span>
              <Button
                className={classes.btn}
                variant="contained"
                onClick={() => setWithdrawDialog(true)}
                disabled={Number(stats.balance) < 50}
                style={Number(stats.balance) < 50 ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              >
                💸 Solicitar Saque — R$ {Number(stats.balance).toFixed(2)} disponível
              </Button>
            </span>
          </Tooltip>
          {Number(stats.balance) < 50 && (
            <Typography variant="body2" style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>
              Mínimo R$ 50,00 para solicitar
            </Typography>
          )}
        </Box>
      )}

      {/* Tabs: Comissões / Saques / Clientes */}
      <Paper className={classes.card} elevation={0}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          TabIndicatorProps={{ style: { background: "#f59e0b" } }}>
          <Tab label="Comissões" className={classes.tab} />
          <Tab label="Saques" className={classes.tab} />
        </Tabs>
        <Divider style={{ borderColor: "rgba(255,255,255,0.1)", marginBottom: 16 }} />

        {tab === 0 && (
          recentCommissions.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography style={{ color: "rgba(255,255,255,0.4)" }}>
                Nenhuma comissão ainda. Compartilhe seu link para começar!
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Empresa</TableCell>
                  <TableCell align="right">Comissão</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentCommissions.map((c) => (
                  <TableRow key={c.id} className={classes.tableRow}>
                    <TableCell>{c.company?.name || `Empresa #${c.companyId}`}</TableCell>
                    <TableCell align="right" style={{ color: "#10b981", fontWeight: 700 }}>
                      R$ {Number(c.amount).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={statusMap[c.status]?.label || c.status}
                        className={chipClass(c.status)} />
                    </TableCell>
                    <TableCell style={{ color: "rgba(255,255,255,0.5)" }}>
                      {moment(c.createdAt).format("DD/MM/YYYY")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}

        {tab === 1 && (
          <WithdrawalsTab authHeader={authHeader} classes={classes} chipClass={chipClass} statusMap={statusMap} />
        )}
      </Paper>

      {/* Dialog saque */}
      <Dialog open={withdrawDialog} onClose={() => setWithdrawDialog(false)}
        PaperProps={{ className: classes.dialogPaper }}>
        <DialogTitle>💸 Solicitar Saque</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: "rgba(255,255,255,0.6)", marginBottom: 16 }}>
            O pagamento será feito via PIX para a chave cadastrada.<br />
            Saldo disponível: <strong style={{ color: "#10b981" }}>R$ {Number(stats.balance).toFixed(2)}</strong>
          </Typography>
          <TextField
            label="Valor (mín. R$ 50,00)"
            variant="outlined"
            fullWidth
            className={classes.dialogInput}
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            type="number"
            inputProps={{ min: 50, max: stats.balance, step: 0.01 }}
            style={{ marginBottom: 16 }}
            InputProps={{
              startAdornment: <InputAdornment position="start" style={{ color: "rgba(255,255,255,0.5)" }}>R$</InputAdornment>
            }}
          />
          <Typography variant="body2" style={{ color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>
            Chave PIX: <strong style={{ color: "#f59e0b" }}>{partner.pixKey || "Não cadastrada"}</strong>
          </Typography>
          <TextField
            label="Observação (opcional)"
            variant="outlined"
            fullWidth
            className={classes.dialogInput}
            value={withdrawNotes}
            onChange={(e) => setWithdrawNotes(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button onClick={() => setWithdrawDialog(false)} style={{ color: "rgba(255,255,255,0.5)" }}>
            Cancelar
          </Button>
          <Button onClick={handleWithdraw} className={classes.btn} variant="contained" disabled={withdrawLoading}>
            {withdrawLoading ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Confirmar Saque"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const WithdrawalsTab = ({ authHeader, classes, chipClass, statusMap }) => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/partners/withdrawals", authHeader()).then(({ data }) => {
      setWithdrawals(data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} style={{ color: "#f59e0b" }} /></Box>;

  if (withdrawals.length === 0) return (
    <Box textAlign="center" py={4}>
      <Typography style={{ color: "rgba(255,255,255,0.4)" }}>Nenhuma solicitação de saque ainda.</Typography>
    </Box>
  );

  return (
    <Table size="small">
      <TableHead className={classes.tableHead}>
        <TableRow>
          <TableCell>Valor</TableCell>
          <TableCell>Chave PIX</TableCell>
          <TableCell align="center">Status</TableCell>
          <TableCell>Data</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {withdrawals.map((w) => (
          <TableRow key={w.id} className={classes.tableRow}>
            <TableCell style={{ color: "#f59e0b", fontWeight: 700 }}>R$ {Number(w.amount).toFixed(2)}</TableCell>
            <TableCell style={{ fontFamily: "monospace", fontSize: 12 }}>{w.pixKey || "-"}</TableCell>
            <TableCell align="center">
              <Chip size="small" label={statusMap[w.status]?.label || w.status} className={chipClass(w.status)} />
            </TableCell>
            <TableCell style={{ color: "rgba(255,255,255,0.5)" }}>{moment(w.createdAt).format("DD/MM/YYYY")}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default PartnerDashboard;
